import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import WebFont from 'webfontloader';

/* --------------------------------------------------------------------------
 * LOADER STORYBOARD
 *
 *    0ms  Greeting fades upward into place
 *  467ms  Greeting begins its upward exit
 *  667ms  Next greeting enters and progress advances
 * 4000ms  Final greeting exit completes; split opening starts
 * -------------------------------------------------------------------------- */
const LOADER_TOTAL_MS = 4000;
const GREETING_COUNT = 6;
const GREETING_EXIT_MS = 200;

const TIMING = {
  greetingHoldMs: (LOADER_TOTAL_MS / GREETING_COUNT) - GREETING_EXIT_MS,
  greetingExitMs: GREETING_EXIT_MS,
  progressSeconds: 0.18,
};

WebFont.load({
  google: {
    families: ['Teko:600']
  }
});

const greetingIn = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 14px, 0) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
`;

const greetingOut = keyframes`
  from {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
  to {
    opacity: 0;
    transform: translate3d(0, -14px, 0) scale(0.97);
  }
`;

const underlineIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) scaleX(0.08);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) scaleX(1);
  }
`;

const LoaderContainer = styled.div`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--color-portfolio-wine);
  z-index: 1000;
`;

const GreetingWrapper = styled.div`
  position: relative;
  padding: 2rem;
  box-sizing: border-box;
  max-width: calc(100vw - 2rem);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      var(--color-portfolio-silver),
      transparent
    );
    opacity: 0;
    transform: translateX(-50%) scaleX(0.08);
    transform-origin: center;
    animation: ${props => props.$visible ? underlineIn : 'none'}
      ${TIMING.greetingExitMs}ms ease-out forwards;
    will-change: opacity, transform;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
      opacity: 1;
      transform: translateX(-50%) scaleX(1);
    }
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const Greeting = styled.div`
  color: var(--color-portfolio-silver);
  font-family: 'Teko', cursive;
  font-size: 7.5rem;
  font-weight: 600;
  height: 1.2em;
  line-height: 1.2;
  letter-spacing: -1px;
  text-shadow: 0 8px 30px rgba(24, 3, 8, 0.3);
  user-select: none;
  white-space: nowrap;
  animation: ${props => props.$visible ? greetingIn : greetingOut}
    ${TIMING.greetingExitMs}ms ease-out forwards;
  will-change: opacity, transform;

  &[data-word='안녕하세요'] {
    font-size: 6rem;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
    transform: none;
  }

  @media (max-width: 768px) {
    font-size: 4.5rem;

    &[data-word='안녕하세요'] {
      font-size: 3.75rem;
    }
  }

  @media (max-width: 480px) {
    font-size: clamp(3.25rem, 17vw, 3.75rem);

    &[data-word='안녕하세요'] {
      font-size: clamp(2.6rem, 13vw, 3rem);
    }
  }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: var(--color-portfolio-silver);
  width: ${props => (props.$progress * 100)}%;
  transition: width ${TIMING.progressSeconds}s ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const greetings = [
  'Hello',
  'Bonjour',
  'नमस्ते',
  'Ciao',
  'Hola',
  '안녕하세요',
];

function Loader({ onLoadingComplete }) {
  const [stage, setStage] = useState(0);
  const totalStages = greetings.length * 2;
  const hasCompleted = stage >= totalStages;
  const currentIndex = Math.min(Math.floor(stage / 2), greetings.length - 1);
  const isVisible = !hasCompleted && stage % 2 === 0;
  const isFinalExit = currentIndex === greetings.length - 1 && !isVisible;

  useEffect(() => {
    if (hasCompleted) {
      onLoadingComplete();
      return undefined;
    }

    const timer = setTimeout(
      () => setStage(previousStage => previousStage + 1),
      isVisible ? TIMING.greetingHoldMs : TIMING.greetingExitMs
    );

    return () => clearTimeout(timer);
  }, [hasCompleted, isVisible, onLoadingComplete]);

  return (
    <LoaderContainer>
      <GreetingWrapper $visible={isVisible}>
        <Greeting
          key={currentIndex}
          $visible={isVisible}
          data-word={greetings[currentIndex]}
        >
          {greetings[currentIndex]}
        </Greeting>
      </GreetingWrapper>
      <ProgressBar
        $progress={isFinalExit ? 1 : currentIndex / greetings.length}
      />
    </LoaderContainer>
  );
}

export default Loader;
